<template>
    <div class="p-sm rounded-sm border border-(--border-subtle)">
        <div class="flex items-center gap-2">
            <i v-if="dragEnabled" class="i-drag" />
            <h2>{{ name }}</h2>
        </div>
        <div class="flex flex-col gap-2">
            <div v-if="roles?.length" class="accordion cursor-pointer" @click="toggle('roles')">
                <div class="flex w-full justify-between">
                    <div class="flex items-center">
                        <i
                            class="i-chevron-right transition-transform"
                            :class="{
                                'rotate-90': expanded.includes('roles'),
                            }"
                        />
                        <p>Roles</p>
                    </div>
                    <TagComponent :value="roles.length.toString()" variant="info" class-name="font-bold text-(--state-info-fg)" />
                </div>
                <Transition name="expand" class="mt-xs">
                    <div v-show="expanded.includes('roles')" class="gap-sm flex flex-col overflow-hidden">
                        <RoleRow
                            v-for="(role, i) in roles"
                            :key="i"
                            no-drag
                            :role="role.name"
                            :level="mapRoleLevelToName(role.service_level_id) ?? null"
                        />
                    </div>
                </Transition>
            </div>

            <div v-if="sites?.length" class="accordion cursor-pointer" @click="toggle('sites')">
                <div class="flex w-full justify-between">
                    <div class="flex items-center">
                        <i
                            class="i-chevron-right transition-transform"
                            :class="{
                                'rotate-90': expanded.includes('sites'),
                            }"
                        />
                        <p>Locations</p>
                    </div>
                    <TagComponent :value="sites.length.toString()" variant="info" class-name="font-bold text-(--state-info-fg)" />
                </div>
                <Transition name="expand" class="mt-xs">
                    <div v-show="expanded.includes('sites')" class="gap-sm flex flex-col overflow-hidden">
                        <GroupRow v-for="(site, i) in sites" :key="i" :name="site.name" />
                    </div>
                </Transition>
            </div>

            <div v-if="regions?.length" class="accordion cursor-pointer" @click="toggle('regions')">
                <div class="flex w-full justify-between">
                    <div class="flex items-center">
                        <i
                            class="i-chevron-right transition-transform"
                            :class="{
                                'rotate-90': expanded.includes('regions'),
                            }"
                        />
                        <p>Regions</p>
                    </div>
                    <TagComponent :value="regions.length.toString()" variant="info" class-name="font-bold text-(--state-info-fg)" />
                </div>
                <Transition name="expand" class="mt-xs">
                    <div v-show="expanded.includes('regions')" class="gap-sm flex flex-col overflow-hidden">
                        <GroupRow v-for="(region, i) in regions" :key="i" :name="region.name" />
                    </div>
                </Transition>
            </div>

            <div v-if="requestTypes?.length" class="accordion cursor-pointer" @click="toggle('requestTypes')">
                <div class="flex w-full justify-between">
                    <div class="flex items-center">
                        <i
                            class="i-chevron-right transition-transform"
                            :class="{
                                'rotate-90': expanded.includes('requestTypes'),
                            }"
                        />
                        <p>Request types</p>
                    </div>
                    <TagComponent :value="requestTypes.length.toString()" variant="info" class-name="aspect-square font-bold text-(--state-info-fg)" />
                </div>
                <Transition name="expand" class="mt-xs">
                    <div v-show="expanded.includes('requestTypes')" class="gap-sm flex flex-col overflow-hidden">
                        <GroupRow v-for="(req, i) in requestTypes" :key="i" :name="req.name" />
                    </div>
                </Transition>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Region, RequestType, Role, Site } from '@/stores/store-interfaces';
import { ref, type PropType, type Ref } from 'vue';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import RoleRow from './role-row.vue';
import GroupRow from './group-row.vue';
import { mapRoleLevelToName } from '@/stores/helpers';

const expanded: Ref<Array<string>> = ref([]);

defineProps({
    name: String,
    roles: Array as PropType<Role[]>,
    sites: Array as PropType<Site[]>,
    regions: Array as PropType<Region[]>,
    requestTypes: Array as PropType<RequestType[]>,
    dragEnabled: {
        type: Boolean,
        default: true,
    },
});

const toggle = (name: string) => {
    if (expanded.value.includes(name)) {
        expanded.value = expanded.value.filter((item: string) => item !== name);
    } else {
        expanded.value.push(name);
    }
};
</script>

<style scoped>
@reference '@/assets/styles/default.css';

.accordion:not(:last-child) {
    padding-bottom: var(--spacing-xs);
    border-bottom: 1px solid var(--color-blue-50);
}
</style>
